import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { formatEther, parseEther } from "ethers/lib/utils";
import { ethers } from "ethers";
import { parseEther as parseEtherViem } from "viem";
import { useAccount, useContractRead, useContractWrite, useNetwork } from "wagmi";
import { signTypedData } from "wagmi/actions";

import * as Con from "./abis";
import { PenaltiesModal } from "./Penalties";
import { TiersModal, tiers } from "./Tiers";

const innerCircleLinks = [
  {
    label: "DuckStarter | DD Inner Circle (80+ DDA)",
    href: "https://telegram.me/collablandbot?start=VFBDI1RFTCNDT01NIy0xMDAyMDQ5MDAyMTE3",
  },
  {
    label: "Duck Fight Club (40+ DDA)",
    href: "https://telegram.me/collablandbot?start=VFBDI1RFTCNDT01NIy0xMDAyMTM2MzExMzI1",
  },
  {
    label: "Duck Beach Club (800+ DDA)",
    href: "https://telegram.me/collablandbot?start=VFBDI1RFTCNDT01NIy0xMDAyMDMzMjIzMTMy",
  },
  {
    label: "Duck Gentlemen Club (10,000+ DDA)",
    href: "https://telegram.me/collablandbot?start=VFBDI1RFTCNDT01NIy0xMDAyMDM2NzA4NTYx",
  },
  {
    label: "Duck Diamond Lounge (40,000+ DDA)",
    href: "https://telegram.me/collablandbot?start=VFBDI1RFTCNDT01NIy0xMDAxOTEwNzY3MjQ4",
  },
];

function AccessTabs() {
  const [stakeAmount, setStakeAmount] = useState("");
  const [unstakeAmount, setUnstakeAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [openPenalties, setOpenPenalties] = useState(false);
  const [openTiers, setOpenTiers] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const { address } = useAccount();
  const { chain } = useNetwork();

  const domain = {
    name: "DuckDAO Token",
    version: "1",
    chainId: chain?.id || 1,
    verifyingContract: Con.Addresses.token as any,
  };

  const Permit = [
    { name: "owner", type: "address" },
    { name: "spender", type: "address" },
    { name: "value", type: "uint256" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" },
  ];

  const types = {
    Permit,
  };

  const ddBalance = useContractRead({
    ...Con.Token,
    functionName: "balanceOf",
    args: [address],
    watch: true,
  });

  const ddaBalance = useContractRead({
    ...Con.Access,
    functionName: "balanceOf",
    args: [address],
    watch: true,
  });

  const ddaData = useContractRead({
    ...Con.Access,
    functionName: "userInfo",
    args: [address],
    watch: true,
  }) as any;

  const dnData = useContractRead({
    ...Con.Token,
    functionName: "nonces",
    args: [address],
  }) as any;

  const stake = useContractWrite({
    ...Con.Access,
    functionName: "addTokensWithPermit",
  });

  const remove = useContractWrite({
    ...Con.Access,
    functionName: "removeTokens",
  });

  const penaltyAmountArg = unstakeAmount === ""
    ? (ddaBalance?.data ?? 0n)
    : parseEtherViem(unstakeAmount || "0");

  const pnData = useContractRead({
    ...Con.Access,
    functionName: "calculatePenalty",
    args: [address, penaltyAmountArg],
  });

  const days = ddaData.isLoading || ddaData.isError
    ? 0
    : Math.floor((Date.now() / 1000 - parseInt(ddaData.data[1]) ?? 0) / 86400);

  const interaction = ddaData.isLoading
    ? "Loading"
    : ddaData.isError
    ? "N/A"
    : parseInt(ddaData.data[1]) === 0 || parseEther("0").eq(ddaBalance.data as any)
    ? "No Access Tokens"
    : `${new Date(parseInt(ddaData.data[1]) * 1000).toLocaleString()} (${days} days ago)`;

  const handleStake = async () => {
    try {
      setLoading(true);

      if (!chain || !address || !dnData || !ddBalance) {
        console.log(chain, address, dnData.data, ddBalance.data);
        throw new Error("No data");
      }

      if (dnData.isLoading || ddBalance.isLoading) {
        throw new Error("Loading");
      }

      if (dnData.error || ddBalance.error || !stake.writeAsync) {
        throw new Error(dnData.error?.message || ddBalance.error?.message || "Error");
      }

      const amount: ethers.BigNumber =
        stakeAmount === "" ? (ddBalance.data as any) : parseEther(stakeAmount);

      const nonce = dnData.data;
      const deadline = new Date().getTime() + 1000 * 60 * 60 * 24 * 365;

      const message = {
        owner: address as any,
        spender: Con.Addresses.access as any,
        value: amount.toString(),
        nonce,
        deadline,
      } as any;

      const signed = await signTypedData({
        domain,
        types,
        message,
        primaryType: "Permit",
      } as any);
      const split = ethers.utils.splitSignature(signed);

      const tx = await stake.writeAsync({
        args: [amount.toString(), deadline, split.v, split.r, split.s],
      } as any);
      await tx.wait();
      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    try {
      setLoading(true);

      if (!chain || !address || !dnData || !ddaBalance) {
        console.log(chain, address, dnData.data, ddaBalance.data);
        throw new Error("No data");
      }

      if (dnData.isLoading || ddaBalance.isLoading) {
        throw new Error("Loading");
      }

      if (dnData.error || ddaBalance.error || !remove.writeAsync) {
        throw new Error(dnData.error?.message || ddaBalance.error?.message || "Error");
      }

      const amount = unstakeAmount === "" ? ddaBalance.data : parseEther(unstakeAmount);
      const tx = await remove.writeAsync({ args: [amount.toString()] } as any);
      await tx.wait();
      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const getTierAccess = (amount?: ethers.BigNumberish) => {
    if (!amount || amount.toString() === "0") {
      return { amount: "0", dsTier: "-", innerTier: "-" };
    }

    return (
      tiers.toReversed().find((tier) => parseEther(tier.amount.toString()).lte(amount)) || {
        amount: "0",
        dsTier: "-",
        innerTier: "-",
      }
    );
  };

  const accountTier = useMemo(
    () => getTierAccess((ddaBalance?.data as any) ?? "0"),
    [ddaBalance?.data],
  );

  const ddBalanceText = ddBalance.isLoading ? "Loading" : formatEther(ddBalance?.data ?? "0");
  const ddaBalanceText = ddaBalance.isLoading ? "Loading" : formatEther(ddaBalance?.data ?? "0");
  const penaltyText = pnData.isLoading ? "Loading" : `${formatEther(pnData.data ?? "0")} DD`;
  const maxStakeAmount = formatEther(ddBalance.data ?? "0");
  const maxUnstakeAmount = formatEther(ddaBalance.data ?? "0");

  const userAmount = (ddaBalance?.data ?? 0n) as bigint;
  const hasAccessTokens = userAmount > 0n;
  const stakingDays = ddaData.isLoading || ddaData.isError || !hasAccessTokens ? null : days;

  const currentTierIndex = useMemo(() => {
    if (!hasAccessTokens) return null;

    let matchIndex: number | null = null;
    tiers.forEach((tier, index) => {
      if (parseEtherViem(tier.amount.toString()) <= userAmount) {
        matchIndex = index;
      }
    });

    return matchIndex;
  }, [hasAccessTokens, userAmount]);

  const currentPenaltyIndex = useMemo(() => {
    if (stakingDays === null) return null;
    if (stakingDays < 10) return 0;
    if (stakingDays < 20) return 1;
    if (stakingDays < 30) return 2;
    if (stakingDays < 60) return 3;
    if (stakingDays < 90) return 4;
    return 5;
  }, [stakingDays]);

  const renderOverviewTab = () => (
    <Card elevation={4} sx={{ borderRadius: 1 }}>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Membership Overview</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Review your staking status before making changes.
        </Typography>

        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
          <Paper variant="outlined" sx={{ borderRadius: 1.5, p: 2 }}>
            <Typography variant="caption" color="text.secondary">STAKING SINCE</Typography>
            <Typography variant="body1" sx={{ mt: 0.75 }}>{interaction}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ borderRadius: 1.5, p: 2 }}>
            <Typography variant="caption" color="text.secondary">ESTIMATED PENALTY</Typography>
            <Typography variant="body1" sx={{ mt: 0.75 }}>{penaltyText}</Typography>
          </Paper>
        </Box>

        <Divider sx={{ my: 2.5 }} />

        <Typography variant="subtitle2" sx={{ mb: 1.25, fontWeight: 600 }}>Inner Circle Access Links</Typography>
        <Box sx={{ display: "grid", gap: 1.25 }}>
          {innerCircleLinks.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              target="_blank"
              rel="noreferrer"
              underline="none"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "action.hover",
                borderRadius: 1,
                px: 1.5,
                py: 1.25,
                color: "primary.main",
                fontSize: 14,
                "&:hover": { bgcolor: "action.selected" },
              }}
            >
              <span>{item.label}</span>
              <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
            </Link>
          ))}
        </Box>
      </CardContent>
    </Card>
  );

  const renderStakeTab = () => (
    <Card elevation={4} sx={{ borderRadius: 1 }}>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Stake DD for DDA</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
          Stake DD tokens to mint DuckDAO Access (DDA).
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          DD available: <strong>{ddBalanceText}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Need help? Contact <Link href="mailto:support@duckdao.io" color="primary.main">support@duckdao.io</Link> or join the{" "}
          <Link href="https://t.me/duckdaolobby" target="_blank" rel="noreferrer" color="primary.main">DuckDAO Lobby</Link>.
        </Typography>

        <Box sx={{ display: "grid", gap: 1.5 }}>
          <TextField
            label="Stake Amount (DD)"
            type="number"
            value={stakeAmount}
            placeholder={maxStakeAmount}
            onChange={(e) => setStakeAmount(e.target.value)}
            fullWidth
            InputProps={{
              endAdornment: (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setStakeAmount(maxStakeAmount)}
                  disabled={ddBalance.isLoading}
                >
                  MAX
                </Button>
              ),
            }}
          />

          <Alert icon={<WarningAmberRoundedIcon fontSize="inherit" />} severity="warning" sx={{ borderRadius: 1 }}>
            Unstaking before 90 days can incur penalties.
          </Alert>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleStake}
              disabled={
                loading || parseEther(stakeAmount || "0").gt(ddBalance.data?.toString() ?? "0")
              }
            >
              Stake DD
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderUnstakeTab = () => (
    <Card elevation={4} sx={{ borderRadius: 1 }}>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Unstake DDA to DD</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
          Unstaking burns DDA and returns DD.
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          DDA available: <strong>{ddaBalanceText}</strong>
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Estimated penalty: <strong>{penaltyText}</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Need help? Contact <Link href="mailto:support@duckdao.io" color="primary.main">support@duckdao.io</Link> or join the{" "}
          <Link href="https://t.me/duckdaolobby" target="_blank" rel="noreferrer" color="primary.main">DuckDAO Lobby</Link>.
        </Typography>

        <Box sx={{ display: "grid", gap: 1.5 }}>
          <TextField
            label="Unstake Amount (DDA)"
            type="number"
            value={unstakeAmount}
            placeholder={maxUnstakeAmount}
            onChange={(e) => setUnstakeAmount(e.target.value)}
            fullWidth
            InputProps={{
              endAdornment: (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setUnstakeAmount(maxUnstakeAmount)}
                  disabled={ddaBalance.isLoading}
                >
                  MAX
                </Button>
              ),
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleRemove}
              disabled={
                loading || parseEther(unstakeAmount || "0").gt(ddaBalance.data?.toString() ?? "0")
              }
            >
              Unstake DDA
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <>
      <PenaltiesModal
        open={openPenalties}
        setOpen={setOpenPenalties}
        currentPenaltyIndex={currentPenaltyIndex}
      />
      <TiersModal
        open={openTiers}
        setOpen={setOpenTiers}
        currentTierIndex={currentTierIndex}
      />

      {loading ? (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 2,
            bgcolor: "rgba(0,0,0,0.7)",
            backdropFilter: "blur(4px)",
          }}
        >
          <CircularProgress color="primary" />
          <Typography variant="h6" sx={{ color: "white", fontWeight: 500 }}>
            Transaction in progress
          </Typography>
        </Box>
      ) : null}

      <Box sx={{ display: "grid", gap: 3 }}>
        <Card elevation={6} sx={{ borderRadius: 1 }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "space-between", flexWrap: "wrap" }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>DuckDAO Membership Access</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75, maxWidth: 760 }}>
                  Stake DD to access DuckDAO Inner Circle groups and DuckSTARTER tiers.
                  Unstaking before 90 days may include a penalty based on staking duration.
                </Typography>
              </Box>
            </Box>

            <Paper sx={{ mt: 2.5, p: 2, borderRadius: 1.5, bgcolor: "action.hover" }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", letterSpacing: 0.5 }}>
                QUICK ACCESS
              </Typography>
              <ButtonGroup fullWidth sx={{ mt: 1.25 }}>
                <Button onClick={() => setOpenTiers(true)} startIcon={<ShieldOutlinedIcon />}>View Tiers</Button>
                <Button onClick={() => setOpenPenalties(true)} startIcon={<InfoOutlinedIcon />}>View Penalties</Button>
              </ButtonGroup>
            </Paper>

            <Box sx={{ mt: 2.5, display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" } }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                <Typography variant="caption" color="text.secondary">DD BALANCE</Typography>
                <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 600 }}>{ddBalanceText}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                <Typography variant="caption" color="text.secondary">DDA BALANCE</Typography>
                <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 600 }}>{ddaBalanceText}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                <Typography variant="caption" color="text.secondary">CURRENT TIER</Typography>
                <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 600 }}>
                  {accountTier.dsTier} | {accountTier.innerTier}
                </Typography>
              </Paper>
            </Box>
          </CardContent>
        </Card>

        <Paper elevation={3} sx={{ borderRadius: 1, p: { xs: 1, sm: 1.5 } }}>
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} variant="fullWidth">
            <Tab label="Overview" />
            <Tab label="Stake" />
            <Tab label="Unstake" />
          </Tabs>
        </Paper>

        <Box>{activeTab === 0 ? renderOverviewTab() : activeTab === 1 ? renderStakeTab() : renderUnstakeTab()}</Box>

        {hasAccessTokens ? (
          <Box
            sx={{
              position: "fixed",
              right: { xs: 12, sm: 20, md: 28 },
              bottom: { xs: 12, sm: 20, md: 24 },
              zIndex: 1200,
            }}
          >
            <Chip
              color="primary"
              variant="filled"
              label={`You are in ${accountTier.dsTier} / ${accountTier.innerTier}`}
              sx={{
                fontWeight: 600,
                boxShadow: 4,
                borderRadius: 1,
              }}
            />
          </Box>
        ) : null}
      </Box>
    </>
  );
}

export const Access = () => {
  return (
    <div>
      <AccessTabs />
    </div>
  );
};
